import { useJanelaDoComite } from '../../api/queries/gmuds'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { dataHoraPorExtenso } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import { JanelaDeImplantacao } from './JanelaDeImplantacao'
import { SolicitacoesDaProximaJanela } from './SolicitacoesDaProximaJanela'

/**
 * A agenda do comitê: as próximas reuniões e janelas de implantação.
 *
 * **A lista pula feriado.** O servidor calcula as datas contra `ref_dia_nao_util`, o mesmo
 * calendário que mede prazo de chamado. Sem isso a tela apontaria uma terça de feriado como próxima
 * reunião — e mandar alguém esperar por uma reunião que não vai acontecer é pior do que não
 * informar data nenhuma.
 *
 * **A ressalva sobre implantar fora da janela fica em destaque, e não em letra miúda.** O sistema
 * não bloqueia: é fato contratual, não regra de software. Uma tela que só mostrasse o horário
 * deixaria o executor concluir que fora dele é impossível — quando o que acontece é que fica caro,
 * e exige justificativa escrita na solicitação.
 *
 * @returns a tela de calendário do comitê
 */
export function PaginaCalendarioDoComite() {
  const { data: janela, isPending, isError, error } = useJanelaDoComite()

  return (
    <div className="space-y-4">
      <header>
        <h1 className="titulo-da-pagina">{TEXTOS.cab.calendario.titulo}</h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.cab.calendario.ajuda}</p>
      </header>

      <JanelaDeImplantacao />

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={5} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar o calendário" />}

      {janela && (
        <div className="cartao p-4">
          <h2 className="text-sm font-semibold text-slate-900">Próximas janelas</h2>
          <ol className="mt-3 divide-y divide-slate-100">
            {janela.proximasAberturas.map((abertura, indice) => (
              <li key={abertura} className="py-2 text-sm">
                <div className="flex items-baseline gap-3">
                  <span className="w-6 shrink-0 text-xs text-slate-400">{indice + 1}</span>
                  <span className="text-slate-800">{dataHoraPorExtenso(abertura)}</span>
                  {indice === 0 && (
                    <span className="rounded-full bg-niteroi-50 px-2 py-0.5 text-xs font-medium text-niteroi-800">
                      a próxima
                    </span>
                  )}
                </div>
                {/* Só abaixo da primeira: o que entra numa janela é o que está aprovado ou com o
                    comitê AGORA, e repetir a lista sob as datas seguintes prometeria um calendário
                    que ninguém marcou. */}
                {indice === 0 && <SolicitacoesDaProximaJanela />}
              </li>
            ))}
          </ol>
          {janela.proximasAberturas.length === 0 && (
            <p className="mt-2 max-w-prose text-sm text-slate-600">
              Não há janela nos próximos 90 dias com a configuração atual. Isso normalmente quer dizer que os dias de
              análise foram marcados como não úteis no calendário do contrato.
            </p>
          )}
        </div>
      )}

      <p className="max-w-prose rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
        {TEXTOS.cab.calendario.foraDaJanela}
      </p>
    </div>
  )
}
